import { useNavigate } from '@tanstack/react-router';
import type { DashboardDto, LayoutItem } from '@vision360/contracts';
import { Check, Pencil, Plus, Star, Trash2, Undo2 } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { widgetRegistry } from '@/widgets/_registry';
import type { AnyWidget } from '@/widgets/_sdk/types';
import { useCreateDashboard, useDashboards, useDeleteDashboard, useUpdateDashboard } from './api';
import { DashboardGrid } from './dashboard-grid';
import { placeNew } from './layout';
import { useLayoutEditor } from './use-layout-editor';
import { type EditorTarget, WidgetCatalog, WidgetEditor } from './widget-dialogs';

/**
 * Dashboards (PRD VS-041): switch, create, rename, delete, edit the grid.
 * `/` shows the default dashboard of mode `web` (TechSpec §9.5).
 */

function pick(list: DashboardDto[], id: string | undefined): DashboardDto | undefined {
  if (id) return list.find((d) => d.id === id);
  return list.find((d) => d.isDefault) ?? list[0];
}

const isTyping = (target: EventTarget | null) =>
  target instanceof HTMLElement &&
  (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName));

export function DashboardPage({
  dashboardId,
  registry = widgetRegistry,
}: {
  dashboardId?: string | undefined;
  registry?: ReadonlyMap<string, AnyWidget>;
}) {
  const { t } = useTranslation('dashboards');
  const navigate = useNavigate();
  const list = useDashboards();
  const create = useCreateDashboard();
  const update = useUpdateDashboard();
  const remove = useDeleteDashboard();
  const dashboard = list.data ? pick(list.data.dashboards, dashboardId) : undefined;

  const editor = useLayoutEditor(dashboard?.id, dashboard?.layout, (id, layout) =>
    update.mutateAsync({ id, patch: { layout } }),
  );
  const [editing, setEditing] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [editorTarget, setEditorTarget] = useState<EditorTarget | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [notice, setNotice] = useState('');
  const switcherId = useId();
  const nameId = useId();

  // Ctrl+Z undoes the last layout change while editing (§9.5).
  const { undo } = editor;
  const dialogOpen = catalogOpen || editorTarget !== null;
  useEffect(() => {
    if (!editing) return;
    const onKey = (event: KeyboardEvent) => {
      if (dialogOpen || isTyping(event.target)) return;
      if ((event.ctrlKey || event.metaKey) && !event.shiftKey && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        undo();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editing, dialogOpen, undo]);

  if (list.isPending) {
    return (
      <output aria-live="polite" className="flex items-center gap-2 text-muted-foreground">
        <Spinner />
        {t('loading')}
      </output>
    );
  }
  if (list.isError) {
    return (
      <div className="max-w-md space-y-3">
        <Alert variant="destructive" role="alert">
          {t('error')}
        </Alert>
        <Button size="sm" variant="outline" onClick={() => void list.refetch()}>
          {t('retry')}
        </Button>
      </div>
    );
  }

  const createAndOpen = async (name: string) => {
    const created = await create.mutateAsync({ name });
    setEditing(true);
    void navigate({ to: '/d/$dashboardId', params: { dashboardId: created.id } });
  };

  if (!dashboard) {
    return (
      <div className="mx-auto max-w-md space-y-3 py-12 text-center">
        <h1 className="text-xl font-semibold">{t('empty.title')}</h1>
        <p className="text-muted-foreground">{t('empty.description')}</p>
        <Button onClick={() => void createAndOpen(t('untitled'))} disabled={create.isPending}>
          <Plus aria-hidden="true" className="h-4 w-4" />
          {t('empty.action')}
        </Button>
      </div>
    );
  }

  const items = editor.items;
  const saveLabel = {
    idle: '',
    saving: t('saving'),
    saved: t('saved'),
    error: t('saveFailed'),
  }[editor.saveState];

  const onSaveWidget = (target: EditorTarget, props: Record<string, unknown>) => {
    const current = editor.current();
    if (target.id) {
      editor.commit(
        current.map((i) =>
          i.i === target.id ? { ...i, widget: { type: target.type, props } } : i,
        ),
      );
    } else {
      const size = registry.get(target.type)?.defaultSize ?? { w: 4, h: 3 };
      const item: LayoutItem = {
        i: crypto.randomUUID(),
        ...placeNew(current, size),
        widget: { type: target.type, props },
      };
      editor.commit([...current, item]);
    }
    setEditorTarget(null);
  };

  const configure = (id: string) => {
    const item = editor.current().find((i) => i.i === id);
    if (item) setEditorTarget({ id, type: item.widget.type, props: item.widget.props });
  };

  const removeWidget = (id: string) => {
    editor.commit(editor.current().filter((i) => i.i !== id));
    setNotice(t('removed'));
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <label htmlFor={switcherId} className="sr-only">
          {t('switcher')}
        </label>
        {renaming ? (
          <form
            className="flex items-center gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              const name = String(new FormData(event.currentTarget).get('name') ?? '').trim();
              if (name) update.mutate({ id: dashboard.id, patch: { name } });
              setRenaming(false);
            }}
          >
            <label htmlFor={nameId} className="sr-only">
              {t('renameLabel')}
            </label>
            <Input
              id={nameId}
              name="name"
              defaultValue={dashboard.name}
              maxLength={120}
              className="h-9 w-64"
              autoFocus
              onKeyDown={(e) => {
                if (e.key !== 'Escape') return;
                e.preventDefault();
                setRenaming(false);
              }}
            />
            <Button type="submit" size="sm">
              <Check aria-hidden="true" className="h-4 w-4" />
              {t('rename')}
            </Button>
          </form>
        ) : (
          <select
            id={switcherId}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm font-semibold"
            value={dashboard.id}
            onChange={(e) =>
              void navigate({ to: '/d/$dashboardId', params: { dashboardId: e.target.value } })
            }
          >
            {list.data.dashboards.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        )}

        <div className="ml-auto flex flex-wrap items-center gap-2">
          <span aria-live="polite" className="text-xs text-muted-foreground">
            {editing ? saveLabel : ''}
            {notice && editing ? ` ${notice}` : ''}
          </span>
          {editing && (
            <>
              <Button size="sm" onClick={() => setCatalogOpen(true)}>
                <Plus aria-hidden="true" className="h-4 w-4" />
                {t('add')}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  editor.undo();
                  setNotice('');
                }}
                disabled={!editor.canUndo}
                title={t('undoHint')}
              >
                <Undo2 aria-hidden="true" className="h-4 w-4" />
                {t('undo')}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setRenaming(true)}>
                {t('rename')}
              </Button>
              {!dashboard.isDefault && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => update.mutate({ id: dashboard.id, patch: { isDefault: true } })}
                >
                  <Star aria-hidden="true" className="h-4 w-4" />
                  {t('makeDefault')}
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  if (!window.confirm(t('deleteConfirm', { name: dashboard.name }))) return;
                  remove.mutate(dashboard.id, {
                    onSuccess: () => {
                      setEditing(false);
                      void navigate({ to: '/' });
                    },
                  });
                }}
              >
                <Trash2 aria-hidden="true" className="h-4 w-4" />
                {t('delete')}
              </Button>
            </>
          )}
          {!editing && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => void createAndOpen(t('newName'))}
              disabled={create.isPending}
            >
              <Plus aria-hidden="true" className="h-4 w-4" />
              {t('new')}
            </Button>
          )}
          <Button
            size="sm"
            variant={editing ? 'default' : 'outline'}
            aria-pressed={editing}
            onClick={() => {
              setEditing((e) => !e);
              setNotice('');
              setRenaming(false);
            }}
          >
            {editing ? (
              <Check aria-hidden="true" className="h-4 w-4" />
            ) : (
              <Pencil aria-hidden="true" className="h-4 w-4" />
            )}
            {editing ? t('done') : t('edit')}
          </Button>
        </div>
      </div>

      {items.length === 0 ? (
        <p className="py-12 text-center text-muted-foreground">
          {t('emptyGrid')} {editing ? t('emptyGridEdit') : ''}
        </p>
      ) : (
        <DashboardGrid
          items={items}
          editing={editing}
          onChange={(next) => editor.commit(next)}
          onConfigure={configure}
          onRemove={removeWidget}
          registry={registry}
        />
      )}

      <WidgetCatalog
        open={catalogOpen}
        onOpenChange={setCatalogOpen}
        registry={registry}
        onPick={(type) => {
          setCatalogOpen(false);
          setEditorTarget({ type, props: { ...registry.get(type)?.defaults } });
        }}
      />
      <WidgetEditor
        target={editorTarget}
        registry={registry}
        onClose={() => setEditorTarget(null)}
        onSave={onSaveWidget}
      />
    </div>
  );
}
